/**
 * CORE-603 — single source of truth for the «Избранное» affordance.
 *
 * The same action used to be drawn two ways: a heart on practices/past lives
 * (which write through the *Favorites endpoints) and a bookmark on
 * events/rooms/lives (which write through saveEvent/unsaveEvent). Two write
 * paths, one read path — `GET /api/user/me/saved` already returns both in one
 * list — so users saw two icons for what is, to them, one action.
 *
 * The glyph and the accessibility wording live here so no call site has to
 * hand-roll the ternary again. Colours deliberately stay at the call site:
 * the icon sits on white pills, solar-filled pills and plain toolbars, and
 * each needs its own contrast.
 *
 * Rule: outline heart = not in favorites, filled heart = in favorites.
 */

/** Callers hold this flag as `boolean`, `boolean | undefined` (optional prop)
 *  or `boolean | null` (BE payload). All of them mean "not in favorites"
 *  when falsy, so accept the lot rather than push casts onto every site. */
type FavoriteFlag = boolean | null | undefined;

/** Ionicons glyph for the favorites toggle. */
export const favoriteIconName = (active: FavoriteFlag): 'heart' | 'heart-outline' =>
  (active ? 'heart' : 'heart-outline');

/** accessibilityLabel / aria-label describing what a press will DO. */
export const favoriteA11yLabel = (active: FavoriteFlag): string =>
  (active ? 'Удалить из избранного' : 'Добавить в избранное');

/**
 * CORE-603 (QA return) — the practices catalog keeps favorites in TWO stores:
 * the legacy per-practice flag (`item.isFavorite`, POST /videopractices/:id/
 * favorite) and the unified saved list (`SavedEventsContext`, the one the
 * profile «Избранное» tab and `GET /api/user/me/saved` read from).
 *
 * They can disagree because their FAILURE semantics differ: on a network error
 * the unified store keeps its optimistic state, while the legacy path rolls the
 * flag back. When that happens the practice is still saved (profile shows it),
 * but a card that read only the legacy flag flipped its heart — and its
 * aria-label — back to «Добавить в избранное». That is exactly the QA divergence:
 * the label no longer matched the actual favorite state.
 *
 * The affordance must present a SINGLE truth. Treat the item as favorited when
 * EITHER store says so: the unified store carries new saves (and survives a
 * legacy hiccup), the legacy flag carries pre-unified saves the profile hasn't
 * backfilled yet. Both `favoriteIconName` and `favoriteA11yLabel` must be fed
 * this one value so the glyph and the wording can never drift apart.
 */
export const favoriteDisplayActive = (
  legacyFavorite: FavoriteFlag,
  unifiedSaved: FavoriteFlag,
): boolean => Boolean(legacyFavorite) || Boolean(unifiedSaved);
